import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import { IReportingQuery } from '@dadohr/shared/domainTypes/reports';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Tooltip, Typography } from '@material-ui/core';
import { compact, mapValues } from 'lodash';
import { CheckCircle2, ChevronRight, Mail, Users, Zap } from 'lucide-react';
import moment from 'moment-timezone';
import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { InfoCircle } from '../../../../components/InfoCircle';
import { Loader } from '../../../../components/Loader';
import { SomethingWentWrong } from '../../../../components/SomethingWentWrong';
import { TeaserBoxFreeTierForExperiences } from '../../../../components/TeaserBox';
import { useErrorLogger } from '../../../../hooks/useErrorLogger';
import { useTrackDuration } from '../../../../hooks/useTrackDuration';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { PageBody } from '../../../../layout/PageBody';
import { PageToolbar, PageToolbarTitle } from '../../../../layout/PageToolbar';
import { Section } from '../../../../layout/Section';
import { useRoutes, useStringQueryParam } from '../../../../routes';
import {
  useCurrentUser,
  useIsCurrentUserDadoEmployee
} from '../../../../services/currentUser';
import { useIsFreeTier } from '../../../../services/organizationTier';
import { useOrgById } from '../../../../services/orgs';
import { useProductAnalytics } from '../../../../services/productAnalytics';
import {
  EmptyStateNoInstances,
  EmptyStateNoReportableTemplates
} from '../../components/EmptyState';
import { PeopleBlockLineChart } from '../../components/PeopleBlockLineChart';
import { ReportBigNumbers } from '../../components/ReportBigNumber';
import { ReportBigNumberAutomation } from '../../components/ReportBigNumber/ReportBigNumberAutomation';
import { ReportCard } from '../../components/ReportCard';
import { ReportFilters } from '../../components/ReportFilters';
import { TasksBlockBarChart } from '../../components/TasksBlockBarChart';
import { useReportsSideNav } from '../../layout';
import {
  parseReportingQueryParams,
  useDefaultReportingQueryParams,
  useReportsOverviewDataViaCf,
  useTimeSavedForOrg
} from '../../services/data';

const LinkContainer = styled('div')((p) => ({
  color: p.theme.custom.colors.blue.w500
}));

const TimeSavedBoxContainer = styled('div')((p) => ({
  backgroundColor: p.theme.custom.colors.green.w100,
  border: `2px solid ${p.theme.custom.colors.green.w300}`,
  borderRadius: p.theme.custom.border.radius(1),
  padding: p.theme.spacing(4)
}));

const TimeSavedBox = ({
  orgName,
  value
}: {
  orgName: string;
  value: number;
}) => {
  const theme = useTheme();
  const { t } = useUnsafeTranslation();
  const duration = useMemo(() => moment.duration(value), [value]);
  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes();

  return (
    <TimeSavedBoxContainer>
      <FlexContainer justifyContent={'space-between'}>
        <Typography variant="h3">
          {orgName} {t('has saved')}{' '}
          <span style={{ color: theme.custom.colors.green.w500 }}>
            {compact([hours && `${hours}h`, minutes && `${minutes}m`]).join(
              ' '
            )}
          </span>{' '}
          {t('of admin time by using Dado')}
        </Typography>
        <Tooltip
          title={
            <div>
              <div>
                {t(
                  'We reach this total by adding up all the tasks, messages and automations that Dado has run and multiplying it by an estimated 5 saved minutes per item.'
                )}
              </div>
              <div>{t('This number is calculated once a day.')}</div>
            </div>
          }
        >
          <FlexContainer
            style={{ color: theme.custom.colors.green.w500 }}
            spacing={0.5}
          >
            <InfoCircle />
            <div>{t('How is this calculated?')}</div>
          </FlexContainer>
        </Tooltip>
      </FlexContainer>
    </TimeSavedBoxContainer>
  );
};

const ReportsOverviewBody = ({
  q,
  setQ,
  defaultQ
}: {
  q: IReportingQuery;
  setQ: (nextQ: IReportingQuery | null) => void;
  defaultQ: IReportingQuery | void;
}) => {
  const { t } = useUnsafeTranslation();
  const { ROUTES } = useRoutes();
  // const [data, loading, error] = useReportsOverviewDataExampleData(q);
  const isFreeTier = useIsFreeTier();
  const [timeSaved] = useTimeSavedForOrg(q.orgId);
  const [org] = useOrgById(q.orgId);
  const [data, loading, error] = useReportsOverviewDataViaCf(q);
  const { trackEvent } = useProductAnalytics();
  const { startTimer, getDuration } = useTrackDuration();
  useErrorLogger('reportsOverviewData', error);

  const tplId = data?.q.tplIds[0];
  useEffect(() => {
    startTimer();
    if (tplId) {
      trackEvent({
        eventType: PAEventType.EXPERIENCE_TPL,
        name: 'Overview Report Page Loaded',
        'Experience Template ID': tplId,
        Explanation: 'Admin user loaded the Overview report page'
      });
    }

    return () => {
      if (tplId) {
        trackEvent({
          eventType: PAEventType.OTHER,
          name: 'Overview Report Page Exited',
          Explanation: 'Admin User loaded the Overview report page',
          'Time On Page': Math.round(getDuration() / 1000)
        });
      }
    };
  }, [trackEvent, tplId, getDuration, startTimer]);

  return (
    <>
      {!!timeSaved && org && (
        <Section spacing={6}>
          <TimeSavedBox
            value={timeSaved}
            orgName={isFreeTier ? 'Acme' : org.data.name}
          />
        </Section>
      )}
      <Section spacing={4}>
        <ReportFilters
          value={q}
          onChange={setQ}
          initialValue={defaultQ}
          additionalTrackingProps={(ev) => ({
            ...ev,
            Report: 'Overview'
          })}
        />
      </Section>
      {loading && <Loader height={500} />}
      {error && <SomethingWentWrong height={500} />}
      {data &&
        (data.data.instances.count ? (
          <>
            <Section spacing={4}>
              <ReportCard
                icon={({ size, colors }) => (
                  <Users size={size} color={colors.green.w500} />
                )}
                title={t('People')}
                subTitle={t('Unique users involved in an experience')}
              >
                <ReportBigNumbers
                  data={mapValues(
                    data.data.people.byActor,
                    (x) => x.counts.total
                  )}
                  actors={data.actors}
                  marginBottom={6}
                  title="people"
                />
                <PeopleBlockLineChart data={data.data.instances} />
              </ReportCard>
            </Section>

            <Section spacing={4}>
              <ReportCard
                icon={({ size, colors }) => (
                  <Mail size={size} color={colors.yellow.w500} />
                )}
                title={t('Messages')}
              >
                <ReportBigNumbers
                  data={mapValues(
                    data.data.messages.byActor,
                    (x) => x.counts.done + x.counts.error
                  )}
                  actors={data.actors}
                  totalLabel={`${t('Total sent')}`}
                  title="messages"
                />
              </ReportCard>
            </Section>

            <Section spacing={4}>
              <ReportCard
                icon={({ size, colors }) => (
                  <CheckCircle2 size={size} color={colors.blue.w500} />
                )}
                title={t('Tasks')}
                actions={
                  <Link to={ROUTES.reports.tasks.url(q)}>
                    <LinkContainer>
                      <FlexContainer>
                        <div>{t('View full report')}</div>
                        <ChevronRight size={16} />
                      </FlexContainer>
                    </LinkContainer>
                  </Link>
                }
              >
                <ReportBigNumbers
                  data={mapValues(
                    data.data.tasks.byActor,
                    (x) =>
                      x.counts.completedLate +
                      x.counts.completedOnTime +
                      x.counts.overdue +
                      x.counts.ongoing
                  )}
                  actors={data.actors}
                  totalLabel={`${t('Total sent')}`}
                  marginBottom={6}
                  title="tasks"
                />
                <TasksBlockBarChart
                  data={data.data.tasks}
                  actors={data.actors}
                />
              </ReportCard>
            </Section>

            <Section spacing={4}>
              <ReportCard
                icon={({ size, colors }) => (
                  <Zap size={size} color={colors.purple.w500} />
                )}
                title={t('Automations')}
              >
                <ReportBigNumberAutomation
                  data={data.data.automations}
                  title="automations"
                />
              </ReportCard>
            </Section>
          </>
        ) : (
          <EmptyStateNoInstances />
        ))}
    </>
  );
};

export const PageReportsOverview = () => {
  const { t } = useUnsafeTranslation();
  const sideNav = useReportsSideNav();
  const { ROUTES, goTo } = useRoutes();

  const [queryParam] = useStringQueryParam('q', '');
  const q = parseReportingQueryParams(queryParam);

  const currentUser = useCurrentUser();
  const isDadoEmployee = useIsCurrentUserDadoEmployee();

  const [defaultQ, loadingQ, errorDefaultQ] = useDefaultReportingQueryParams(
    currentUser
  );
  useErrorLogger('default q', errorDefaultQ);

  const setQ = (nextQ: IReportingQuery | null) =>
    goTo(ROUTES.reports.overview.url(nextQ));

  const actualQ = q || defaultQ;

  const { startTimer, getDuration } = useTrackDuration();
  const { trackEvent } = useProductAnalytics();

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        startTimer();
      } else {
        trackEvent({
          eventType: PAEventType.OTHER,
          name: 'Overview Report Page Exited',
          Explanation: 'Admin User loaded the Overview report page',
          'Time On Page': Math.round(getDuration() / 1000)
        });
      }
    };
    const handleWindowClose = (e: any) => {
      e.preventDefault();
      trackEvent({
        eventType: PAEventType.OTHER,
        name: 'Overview Report Page Exited',
        Explanation: 'Admin User loaded the Overview report page',
        'Time On Page': Math.round(getDuration() / 1000)
      });
    };

    startTimer();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleWindowClose);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleWindowClose);
    };
  });

  // TODO check if user can view the requested tpls
  return (
    <PageBody noTopPadding sideNav={sideNav}>
      <TeaserBoxFreeTierForExperiences>
        {t(
          'This is a preview of the reporting Dado offers for Experiences, using sample data.'
        )}
      </TeaserBoxFreeTierForExperiences>
      <PageToolbar>
        <PageToolbarTitle>
          <Typography component="h1" variant="h1">
            {t('Overview report')}
          </Typography>
        </PageToolbarTitle>
      </PageToolbar>

      {!actualQ && loadingQ && <Loader height={500} />}
      {actualQ &&
        defaultQ &&
        (actualQ.tplIds.length ? (
          <ReportsOverviewBody q={actualQ} setQ={setQ} defaultQ={defaultQ} />
        ) : (
          <EmptyStateNoReportableTemplates />
        ))}
    </PageBody>
  );
};
